import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Wrench, FolderGit2, Briefcase, ClipboardCheck, MessageSquare, Award, FolderCheck,
  ArrowUpRight, Sparkles, CircleCheck, ScanLine,
} from 'lucide-react';
import { AsyncBoundary, Drawer } from '@kollez/design-system';
import { useProfile360, type Profile360Dim } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';

/**
 * /profile-360 (F93a) — direct port of kollez/pages/student/profile-360.html.
 *   .bigring conic-gradient overall + SVG radar (n axes + Day-1 baseline dashed polygon)
 *   Left aside: readiness ring, biggest wins, how-it-works details
 *   Right: 8 dim rows w/ weight + status badge + evidence chips + gap arrow + per-dim bar
 *   Drawer per dim with Evidence checks + Nexie "What would raise it" + action CTAs
 */

const ICONS: Record<string, typeof GraduationCap> = {
  identity: FolderCheck,
  academics: GraduationCap,
  skills: Wrench,
  projects: FolderGit2,
  verbal_comm: MessageSquare,
  aptitude: ClipboardCheck,
  interview_prep: Briefcase,
  documents: Award,
};

function iconFor(key: string) {
  return ICONS[key] ?? FolderCheck;
}

export function StudentProfile360Page() {
  const { user } = useAuthStore();
  const { data, isLoading, error, refetch } = useProfile360();
  const [openDim, setOpenDim] = useState<Profile360Dim | null>(null);

  const readiness = data?.readiness;
  const overall = readiness?.overall ?? 0;
  const dims = readiness?.dimensions ?? [];

  const readinessLabel = overall >= 80 ? 'Ready for Day 1 roles'
    : overall >= 60 ? 'On track for Day 1'
    : 'Needs work before campus season';

  const readinessTone = overall >= 80 ? 'badge-success' : overall >= 60 ? 'badge-info' : 'badge-warning';

  // Biggest wins available: lowest-scored dims by weight × gap × 0.35
  const wins = useMemo(() => {
    return [...dims]
      .map((d) => ({ dim: d, gain: ((100 - d.score) * d.weight / 100) * 0.35 }))
      .sort((a, b) => b.gain - a.gain)
      .slice(0, 3);
  }, [dims]);

  const radar = useMemo(() => renderRadar(dims), [dims]);

  return (
    <div className="stack">
      <style>{`
        .p360 { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .p360 { grid-template-columns: 360px minmax(0,1fr); } .p360-side { position: sticky; top: 76px; } }
        .bigring { width: 132px; height: 132px; border-radius: 50%; display: grid; place-items: center; background: conic-gradient(rgb(var(--ink-accent)) calc(var(--v) * 1%), rgb(var(--bg-tertiary)) 0); position: relative; margin: 0 auto; }
        .bigring::before { content: ""; position: absolute; inset: 10px; border-radius: 50%; background: rgb(var(--bg-primary)); }
        .bigring > div { position: relative; text-align: center; }
        .bigring b { font-family: "Newsreader", Georgia, serif; font-size: 2.75rem; font-weight: 500; line-height: 1; display: block; }
        .radar { width: 100%; max-width: 320px; margin: 0 auto; display: block; }
        .radar text { font-size: 10px; fill: rgb(var(--ink-secondary)); font-family: inherit; }
        .dimrow { display: grid; grid-template-columns: 36px minmax(0,1fr) auto; gap: 14px; padding: 16px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); align-items: start; cursor: pointer; }
        .dimrow:last-child { border-bottom: 0; }
        .dimrow:hover { background: rgb(var(--bg-secondary)); }
        .dimrow .sc { font-family: "Newsreader", Georgia, serif; font-size: 1.625rem; font-weight: 500; line-height: 1; text-align: right; }
        .dimrow .ev { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
        .lowdim .sc { color: rgb(var(--ink-warning)); }
        .conf { font-size: .6875rem; font-weight: 600; padding: 1px 6px; border-radius: 4px; }
        .conf.hi { background: rgb(var(--bg-success)); color: rgb(var(--ink-success)); }
        .conf.lo { background: rgb(var(--bg-warning)); color: rgb(var(--ink-warning)); }
      `}</style>

      <div className="page-header">
        <div className="flex items-center gap-4 min-w-0">
          <span className="avatar avatar-xl">{(user?.fullName ?? 'S').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
          <div className="ph-text">
            <p className="eyebrow">Your placement readiness</p>
            <h1 className="h-page">{user?.fullName ?? 'You'}</h1>
            <p className="subhead">Everything about your placement readiness in one place — what's proven, what's missing, and what to do next.</p>
          </div>
        </div>
        <div className="ph-actions">
          {/* 'Fill from resume' was a non-functional cosmetic button. Removed — the
              readiness score is computed server-side from real evidence; there's no
              client-side auto-fill. */}
          <Link className="btn btn-primary" to="/upload-marks"><ScanLine size={14} />Upload marksheet</Link>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={isLoading}
        error={error}
        empty={!isLoading && !readiness}
        emptyTitle="Readiness not computed yet"
        emptyHint="Complete your profile, then tap Recompute to score."
        onRetry={() => void refetch()}
      >
        {readiness && (
          <div className="p360">
            <aside className="p360-side grid gap-4">
              <section className="card card-pad grid gap-3 text-center">
                <p className="eyebrow m-0">Placement readiness</p>
                <div className="bigring" style={{ ['--v' as string]: overall } as React.CSSProperties}>
                  <div>
                    <b>{overall}</b>
                    <span className="helper">out of 100</span>
                  </div>
                </div>
                <div>
                  <span className={`badge ${readinessTone}`}>{readinessLabel}</span>
                </div>
                <svg className="radar" viewBox="-44 0 408 304" role="img" aria-label="Scores across dimensions" dangerouslySetInnerHTML={{ __html: radar }} />
              </section>

              <section className="card card-pad grid gap-2">
                <h2 className="h-section">Biggest wins available</h2>
                <div className="grid gap-2">
                  {wins.map(({ dim, gain }) => {
                    const Icon = iconFor(dim.key);
                    return (
                      <button
                        key={dim.key}
                        className="flex items-center gap-3 well p-3 text-left"
                        onClick={() => setOpenDim(dim)}
                        style={{ background: 'rgb(var(--bg-secondary))', borderRadius: 6, border: '1px solid rgb(var(--line-tertiary))' }}
                      >
                        <span className="logo-tile"><Icon size={14} /></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{dim.label}</div>
                          <div className="cell-sub">Weight {dim.weight}%</div>
                        </div>
                        <span className="badge badge-info">+{Math.max(1, Math.round(gain))}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <details className="card card-pad">
                <summary className="font-semibold cursor-pointer">How this score works</summary>
                <div className="grid gap-2 mt-3 text-sm">
                  <p className="m-0">
                    Nexie weights eight areas the way companies that hired at your college did in recent seasons.
                    Verified evidence counts fully; self-reported evidence counts half.
                  </p>
                  <p className="m-0">
                    <b>Companies never see this number.</b> They see only your verified records. Your placement officer sees the same view you do.
                  </p>
                  <p className="m-0">It's a guide, not a gate: no drive uses it to decide eligibility.</p>
                  <Link className="link justify-self-start" to="/nexie">
                    Something looks wrong? Ask Nexie.
                  </Link>
                </div>
              </details>
            </aside>

            <div className="grid gap-5 min-w-0">
              <section className="card">
                <div className="card-head">
                  <h2 className="h-section">The eight areas</h2>
                  <span className="helper">Tap one for evidence and next steps</span>
                </div>
                {dims.map((d) => {
                  const Icon = iconFor(d.key);
                  const low = d.score < 65;
                  return (
                    <div
                      key={d.key}
                      className={`dimrow${low ? ' lowdim' : ''}`}
                      tabIndex={0}
                      onClick={() => setOpenDim(d)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOpenDim(d); }}
                    >
                      <span className="logo-tile"><Icon size={14} /></span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <b>{d.label}</b>
                          <span className="cell-sub">weight {d.weight}%</span>
                          <span className={`badge ${d.confidence === 'hi' ? 'badge-success' : 'badge-warning'}`}>
                            {d.confidence === 'hi' ? 'Verified' : 'Self-reported'}
                          </span>
                        </div>
                        {d.evidence.length > 0 && (
                          <div className="ev">
                            {d.evidence.map((e, i) => <span key={i} className="chip">{e}</span>)}
                          </div>
                        )}
                        <div className="text-sm mt-2" style={{ color: 'rgb(var(--ink-secondary))' }}>
                          <ArrowUpRight size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                          {gapForDim(d)}
                        </div>
                      </div>
                      <div className="grid gap-1 justify-items-end">
                        <span className="sc">{d.score}</span>
                        <div className={`bar${low ? ' warning' : ''}`} style={{ width: 72 }}>
                          <span style={{ width: `${d.score}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>

              {/* Removed 'Your story so far' — the four items were hardcoded strings,
                  not driven by any real evidence source. Users saw the same 'Academics
                  verified · Semesters 1–6' line regardless of their actual state. */}

              <section className="card card-pad grid gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="h-section">What companies see when you apply</h2>
                  <Link className="link text-sm" to="/settings/privacy">Privacy settings</Link>
                </div>
                <div className="well p-4 grid gap-1 text-sm">
                  <b className="text-base">
                    {user?.fullName ?? '—'} <span className="badge badge-success ml-1">Verified</span>
                  </b>
                  <span>Skills, projects and internships as you have them on your profile.</span>
                  <span className="helper">No readiness score, no test scores from other companies, no phone number until shortlisted.</span>
                </div>
              </section>
            </div>
          </div>
        )}
      </AsyncBoundary>

      <Drawer open={Boolean(openDim)} onClose={() => setOpenDim(null)} title={openDim?.label ?? ''} size="md">
        {openDim && (
          <>
            <p className="eyebrow">
              Weight {openDim.weight}% · {openDim.confidence === 'hi' ? 'Verified' : 'Self-reported'}
            </p>
            <h2 className="h-section">{openDim.label} · {openDim.score}</h2>
            {openDim.evidence.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold mb-2">Evidence</h3>
                {openDim.evidence.map((e, i) => (
                  <div key={i} className="qcheck ok"><CircleCheck size={14} /><span>{e}</span></div>
                ))}
              </div>
            )}
            <div className="ai-suggest mt-4">
              <Sparkles size={14} />
              <div><b>What would raise it.</b> {gapForDim(openDim)}</div>
            </div>
            {actionsFor(openDim).map((a) => (
              <Link key={a.href} className="btn btn-primary justify-self-start mt-3" to={a.href}>{a.label}</Link>
            ))}
          </>
        )}
      </Drawer>
    </div>
  );
}

function actionsFor(d: Profile360Dim): Array<{ label: string; href: string }> {
  const map: Record<string, Array<{ label: string; href: string }>> = {
    academics: [{ label: 'Upload marksheet', href: '/upload-marks' }],
    skills: [{ label: 'Take a skills check', href: '/assess/exams' }],
    projects: [{ label: 'Update projects', href: '/resume-builder' }],
    interview_prep: [{ label: 'Book a mock interview', href: '/mock' }],
    aptitude: [{ label: 'Practise aptitude', href: '/prep' }],
    documents: [{ label: 'Add documents', href: '/profile' }],
    verbal_comm: [{ label: 'Book a mock interview', href: '/mock' }],
    identity: [{ label: 'Update profile', href: '/profile' }],
  };
  return map[d.key] ?? [{ label: 'Update profile', href: '/profile' }];
}

function gapForDim(d: Profile360Dim): string {
  if (d.score >= 85) return 'Already strong — keep it up.';
  if (d.confidence === 'lo') return `Verify this to raise ${d.label.toLowerCase()}.`;
  return `Add proof or another piece of evidence to lift ${d.label.toLowerCase()}.`;
}

function renderRadar(dims: Profile360Dim[]): string {
  if (dims.length < 3) return '';
  const cx = 160, cy = 150, R = 105, n = dims.length;
  const pt = (i: number, v: number): [number, number] => {
    const a = -Math.PI / 2 + i * 2 * Math.PI / n;
    return [cx + Math.cos(a) * R * v / 100, cy + Math.sin(a) * R * v / 100];
  };
  const escape = (s: string) => s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' })[c]!);

  let svg = '';
  for (const l of [25, 50, 75, 100]) {
    svg += `<polygon fill="none" stroke="rgb(var(--line-tertiary))" points="${dims.map((_, i) => pt(i, l).join(',')).join(' ')}"/>`;
  }
  dims.forEach((d, i) => {
    const p = pt(i, 100);
    const q = pt(i, 122);
    const anchor = Math.abs(q[0] - cx) < 10 ? 'middle' : q[0] > cx ? 'start' : 'end';
    svg += `<line x1="${cx}" y1="${cy}" x2="${p[0]}" y2="${p[1]}" stroke="rgb(var(--line-tertiary))"/>`;
    svg += `<text x="${q[0]}" y="${q[1] + 3}" text-anchor="${anchor}">${escape(d.label)}</text>`;
  });
  svg += `<polygon fill="rgb(var(--ink-accent) / .18)" stroke="rgb(var(--ink-accent))" stroke-width="2" points="${dims.map((d, i) => pt(i, d.score).join(',')).join(' ')}"/>`;
  // Removed the 'typical Day 1 hire' dashed baseline — it was a hardcoded [80,75,70,…]
  // array, not real cohort data. When we compute a real cohort baseline server-side
  // it lands back here as an extra polygon derived from the API response.
  return svg;
}
